import { createSlice } from "@reduxjs/toolkit";
import { getInitiatives } from "./getInitiatives";
import { getInitiativeDetail } from "./getInitiativeDetail";
import { createInitiative } from "./createInitiative";
import { updateInitiative } from "./updateInitiative";
import { deleteInitiative } from "./deleteInitiative";
import { addMilestone } from "./addMilestone";
import { updateMilestone } from "./updateMilestone";
import { deleteMilestone } from "./deleteMilestone";
import { addComment } from "./addComment";
import { deleteComment } from "./deleteComment";
import { uploadFile } from "./uploadFile";
import { deleteFile } from "./deleteFile";
import { getInitiativeCounts } from "./getCounts";

const initialState = {
  list: {
    success: false,
    data: [],
    isLoading: true,
    errMsg: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 20,
      totalPages: 0,
    },
  },
  detail: {
    success: false,
    data: null,
    isLoading: false,
    errMsg: null,
  },
  counts: {
    success: false,
    data: {},
    isLoading: false,
    errMsg: null,
  },
  create: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
  update: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
  delete: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
  milestone: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
  comment: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
  file: {
    success: false,
    isLoading: false,
    errMsg: null,
  },
};

const initiativesSlice = createSlice({
  name: "initiatives",
  initialState,
  reducers: {
    resetCreateState: (state) => {
      state.create = initialState.create;
    },
    resetUpdateState: (state) => {
      state.update = initialState.update;
    },
    resetDeleteState: (state) => {
      state.delete = initialState.delete;
    },
    resetDetailState: (state) => {
      state.detail = initialState.detail;
    },
    addLocalComment: (state, action) => {
      if (state.detail.data) {
        const comments = state.detail.data.comments || [];
        state.detail.data.comments = [...comments, action.payload];
      }
    },
    removeLocalComment: (state, action) => {
      if (state.detail.data && state.detail.data.comments) {
        state.detail.data.comments = state.detail.data.comments.filter(
          (c) => c.id !== action.payload
        );
      }
    },
    addLocalFile: (state, action) => {
      if (state.detail.data) {
        const files = state.detail.data.files || [];
        state.detail.data.files = [...files, action.payload];
      }
    },
    removeLocalFile: (state, action) => {
      if (state.detail.data && state.detail.data.files) {
        state.detail.data.files = state.detail.data.files.filter(
          (f) => f.id !== action.payload
        );
      }
    },
    addLocalMilestone: (state, action) => {
      if (state.detail.data) {
        const milestones = state.detail.data.milestones || [];
        state.detail.data.milestones = [...milestones, action.payload];
      }
    },
    updateLocalMilestone: (state, action) => {
      if (state.detail.data && state.detail.data.milestones) {
        const index = state.detail.data.milestones.findIndex(
          (m) => m.id === action.payload.id
        );
        if (index !== -1) {
          state.detail.data.milestones[index] = action.payload;
        }
      }
    },
    removeLocalMilestone: (state, action) => {
      if (state.detail.data && state.detail.data.milestones) {
        state.detail.data.milestones = state.detail.data.milestones.filter(
          (m) => m.id !== action.payload
        );
      }
    },
  },
  extraReducers: (builder) => {
    // Get all initiatives
    builder
      .addCase(getInitiatives.pending, (state, action) => {
        state.list.isLoading = true;
        state.list.errMsg = null;
        if (action.meta?.arg?.page === 1) {
          state.list.data = [];
        }
      })
      .addCase(getInitiatives.fulfilled, (state, action) => {
        state.list.success = true;
        state.list.isLoading = false;
        if (action.meta?.arg?.page > 1) {
          state.list.data = [...state.list.data, ...action.payload.data];
        } else {
          state.list.data = action.payload.data;
        }
        state.list.pagination = action.payload.pagination;
      })
      .addCase(getInitiatives.rejected, (state, action) => {
        state.list.isLoading = false;
        state.list.success = false;
        state.list.errMsg = action.payload;
      });

    // Get initiative detail
    builder
      .addCase(getInitiativeDetail.pending, (state) => {
        state.detail.isLoading = true;
        state.detail.errMsg = null;
      })
      .addCase(getInitiativeDetail.fulfilled, (state, action) => {
        state.detail.success = true;
        state.detail.isLoading = false;
        state.detail.data = action.payload.data;
      })
      .addCase(getInitiativeDetail.rejected, (state, action) => {
        state.detail.isLoading = false;
        state.detail.success = false;
        state.detail.errMsg = action.payload;
      });

    // Get counts
    builder
      .addCase(getInitiativeCounts.pending, (state) => {
        state.counts.isLoading = true;
      })
      .addCase(getInitiativeCounts.fulfilled, (state, action) => {
        state.counts.success = true;
        state.counts.isLoading = false;
        state.counts.data = action.payload.data;
      })
      .addCase(getInitiativeCounts.rejected, (state, action) => {
        state.counts.isLoading = false;
        state.counts.errMsg = action.payload;
      });

    // Create initiative
    builder
      .addCase(createInitiative.pending, (state) => {
        state.create.isLoading = true;
        state.create.errMsg = null;
        state.create.success = false;
      })
      .addCase(createInitiative.fulfilled, (state, action) => {
        state.create.success = true;
        state.create.isLoading = false;
        state.list.data = [action.payload.data, ...state.list.data];
      })
      .addCase(createInitiative.rejected, (state, action) => {
        state.create.isLoading = false;
        state.create.success = false;
        state.create.errMsg = action.payload;
      });

    // Update initiative
    builder
      .addCase(updateInitiative.pending, (state) => {
        state.update.isLoading = true;
        state.update.errMsg = null;
        state.update.success = false;
      })
      .addCase(updateInitiative.fulfilled, (state, action) => {
        state.update.success = true;
        state.update.isLoading = false;
        // Update in list
        const index = state.list.data.findIndex(
          (i) => i.id === action.payload.data.id
        );
        if (index !== -1) {
          state.list.data[index] = action.payload.data;
        }
        // Update detail if viewing
        if (state.detail.data?.id === action.payload.data.id) {
          state.detail.data = { ...state.detail.data, ...action.payload.data };
        }
      })
      .addCase(updateInitiative.rejected, (state, action) => {
        state.update.isLoading = false;
        state.update.success = false;
        state.update.errMsg = action.payload;
      });

    // Delete initiative
    builder
      .addCase(deleteInitiative.pending, (state) => {
        state.delete.isLoading = true;
        state.delete.errMsg = null;
        state.delete.success = false;
      })
      .addCase(deleteInitiative.fulfilled, (state, action) => {
        state.delete.success = true;
        state.delete.isLoading = false;
        state.list.data = state.list.data.filter(
          (i) => i.id !== action.meta.arg
        );
      })
      .addCase(deleteInitiative.rejected, (state, action) => {
        state.delete.isLoading = false;
        state.delete.success = false;
        state.delete.errMsg = action.payload;
      });

    // Milestones
    builder
      .addCase(addMilestone.pending, (state) => {
        state.milestone.isLoading = true;
      })
      .addCase(addMilestone.fulfilled, (state, action) => {
        state.milestone.success = true;
        state.milestone.isLoading = false;
      })
      .addCase(addMilestone.rejected, (state, action) => {
        state.milestone.isLoading = false;
        state.milestone.errMsg = action.payload;
      })
      .addCase(updateMilestone.pending, (state) => {
        state.milestone.isLoading = true;
      })
      .addCase(updateMilestone.fulfilled, (state, action) => {
        state.milestone.success = true;
        state.milestone.isLoading = false;
      })
      .addCase(updateMilestone.rejected, (state, action) => {
        state.milestone.isLoading = false;
        state.milestone.errMsg = action.payload;
      })
      .addCase(deleteMilestone.pending, (state) => {
        state.milestone.isLoading = true;
      })
      .addCase(deleteMilestone.fulfilled, (state) => {
        state.milestone.success = true;
        state.milestone.isLoading = false;
      })
      .addCase(deleteMilestone.rejected, (state, action) => {
        state.milestone.isLoading = false;
        state.milestone.errMsg = action.payload;
      });

    // Comments
    builder
      .addCase(addComment.pending, (state) => {
        state.comment.isLoading = true;
      })
      .addCase(addComment.fulfilled, (state) => {
        state.comment.success = true;
        state.comment.isLoading = false;
      })
      .addCase(addComment.rejected, (state, action) => {
        state.comment.isLoading = false;
        state.comment.errMsg = action.payload;
      })
      .addCase(deleteComment.pending, (state) => {
        state.comment.isLoading = true;
      })
      .addCase(deleteComment.fulfilled, (state) => {
        state.comment.success = true;
        state.comment.isLoading = false;
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.comment.isLoading = false;
        state.comment.errMsg = action.payload;
      });

    // Files
    builder
      .addCase(uploadFile.pending, (state) => {
        state.file.isLoading = true;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.file.success = true;
        state.file.isLoading = false;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.file.isLoading = false;
        state.file.errMsg = action.payload;
      })
      .addCase(deleteFile.pending, (state) => {
        state.file.isLoading = true;
      })
      .addCase(deleteFile.fulfilled, (state) => {
        state.file.success = true;
        state.file.isLoading = false;
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.file.isLoading = false;
        state.file.errMsg = action.payload;
      });
  },
});

export const {
  resetCreateState,
  resetUpdateState,
  resetDeleteState,
  resetDetailState,
  addLocalComment,
  removeLocalComment,
  addLocalFile,
  removeLocalFile,
  addLocalMilestone,
  updateLocalMilestone,
  removeLocalMilestone,
} = initiativesSlice.actions;

export default initiativesSlice.reducer;
export * from "./getInitiatives";
export * from "./getInitiativeDetail";
export * from "./createInitiative";
export * from "./updateInitiative";
export * from "./deleteInitiative";
export * from "./addMilestone";
export * from "./updateMilestone";
export * from "./deleteMilestone";
export * from "./addComment";
export * from "./deleteComment";
export * from "./uploadFile";
export * from "./deleteFile";
export * from "./getCounts";
